import { useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { LineChart, Line, XAxis, YAxis, Tooltip, ReferenceLine, ResponsiveContainer, CartesianGrid } from "recharts";
import { TrendingDown, TrendingUp, Minus } from "lucide-react";

type Measurement = {
  id: string;
  measured_at: string;
  intensity_0_10?: number | null;
  intensity_0_100?: number | null;
  context?: string | null;
};

type Target = {
  id: string;
  symptom_name: string;
  baseline?: number | null;
  therapeutic_goal?: number | null;
  priority?: number | null;
};

function to0_10(m: Measurement): number | null {
  if (m.intensity_0_10 != null) return Number(m.intensity_0_10);
  if (m.intensity_0_100 != null) return Number(m.intensity_0_100) / 10;
  return null;
}

export function TargetSymptomTimeline({ target, measurements }: { target: Target; measurements: Measurement[] }) {
  const data = useMemo(() => {
    return measurements
      .filter((m) => to0_10(m) != null)
      .sort((a, b) => new Date(a.measured_at).getTime() - new Date(b.measured_at).getTime())
      .map((m) => ({
        t: new Date(m.measured_at).getTime(),
        label: new Date(m.measured_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" }),
        v: to0_10(m),
        ctx: m.context ?? "",
      }));
  }, [measurements]);

  const first = data[0]?.v;
  const last = data[data.length - 1]?.v;
  const goal = target.therapeutic_goal;
  const baseline = target.baseline;

  const trend =
    first != null && last != null && Math.abs((last as number) - (first as number)) >= 0.5
      ? (last as number) < (first as number)
        ? "melhorando"
        : "piorando"
      : "estável";

  const distanceToGoal = last != null && goal != null ? Math.abs((last as number) - goal) : null;

  return (
    <Card className="p-4 space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="font-medium text-sm">{target.symptom_name}</div>
          <div className="text-[11px] text-muted-foreground">
            baseline {baseline ?? "—"} → meta {goal ?? "—"} • {data.length} medições
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Badge variant="outline" className="text-[10px] flex items-center gap-1">
            {trend === "melhorando" ? <TrendingDown className="h-3 w-3 text-primary" /> : trend === "piorando" ? <TrendingUp className="h-3 w-3 text-destructive" /> : <Minus className="h-3 w-3" />}
            {trend}
          </Badge>
          {distanceToGoal != null && (
            <Badge variant="secondary" className="text-[10px]">
              {distanceToGoal <= 1 ? "próximo da meta" : `Δ meta ${distanceToGoal.toFixed(1)}`}
            </Badge>
          )}
        </div>
      </div>
      {data.length === 0 ? (
        <div className="text-xs text-muted-foreground">Sem medições registradas ainda.</div>
      ) : (
        <div className="h-40 w-full">
          <ResponsiveContainer>
            <LineChart data={data} margin={{ top: 6, right: 12, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} />
              <YAxis domain={[0, 10]} tick={{ fontSize: 10 }} />
              <Tooltip
                contentStyle={{ fontSize: 11 }}
                formatter={(v: any) => [`${Number(v).toFixed(1)}/10`, "intensidade"]}
                labelFormatter={(l) => l}
              />
              {baseline != null && <ReferenceLine y={Number(baseline)} stroke="hsl(var(--muted-foreground))" strokeDasharray="4 4" label={{ value: "baseline", fontSize: 9, position: "insideTopRight" }} />}
              {goal != null && <ReferenceLine y={Number(goal)} stroke="hsl(var(--primary))" strokeDasharray="4 4" label={{ value: "meta", fontSize: 9, position: "insideBottomRight" }} />}
              <Line type="monotone" dataKey="v" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}
